import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Bell, Check, ChevronDown, ChevronRight, Settings, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const SEV = {
  attention: { label: "Needs attention", cls: "bg-red-100 text-red-800 border-red-200" },
  changed: { label: "Changed", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  fyi: { label: "FYI", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};
const CHIPS = [["attention", "Needs attention"], ["changed", "Changed"], ["fyi", "FYI"], ["unread", "Unread"]];
const SETTINGS = [
  ["level_changes", "Level changes", "The headset raised or lowered a level"],
  ["low_success", "Low success suggestions", "Success is low: consider lowering"],
  ["high_success", "High success suggestions", "Success is high: consider raising"],
];

// One notification per patient per day; lines grouped by module (collapsed until opened).
export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0, total: 0, page: 1, page_size: 50 });
  const [open, setOpen] = useState(false);
  const [chip, setChip] = useState(null);
  const [page, setPage] = useState(1);
  const [clinician, setClinician] = useState("");
  const [clinicians, setClinicians] = useState([]);
  const [view, setView] = useState("list");           // list | settings
  const [settings, setSettings] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    const params = { page, page_size: 50 };
    if (chip === "unread") params.unread = true; else if (chip) params.severity = chip;
    if (clinician) params.clinician_id = clinician;
    try { const r = await api.get("/notifications", { params }); setData(r.data); } catch (_e) { /* ignore */ }
  }, [page, chip, clinician]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (user?.role === "admin") api.get("/users").then((r) => setClinicians(r.data.filter((u) => u.role === "therapist"))).catch(() => {});
  }, [user]);

  const act = async (fn) => { try { await fn(); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const go = (n, to) => { act(() => api.post(`/notifications/${n.id}/read`)); setOpen(false); navigate(to); };
  const openSettings = async () => {
    try { const r = await api.get("/notification-settings"); setSettings(r.data); setView("settings"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (k, v) => {
    const next = { ...settings, [k]: v };
    setSettings(next);
    try { await api.put("/notification-settings", { [k]: v }); } catch (e) { setSettings(settings); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const groupByModule = (lines) => lines.reduce((acc, l) => { (acc[l.game || "other"] = acc[l.game || "other"] || []).push(l); return acc; }, {});
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) { setView("list"); load(); } }}>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Notifications" data-testid="notification-bell"
          className="relative h-9 w-9 grid place-items-center rounded-lg text-slate-200 hover:bg-white/10">
          <Bell className="h-5 w-5" strokeWidth={1.75} />
          {data.unread > 0 && (
            <span data-testid="notification-count"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-fuchsia-500 text-white text-[10px] font-semibold grid place-items-center">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" className="w-[30rem] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-900">{view === "settings" ? "Notification settings" : "Notifications"}</div>
          <div className="flex items-center gap-3">
            {view === "list" && data.unread > 0 && (
              <button className="text-xs text-purple-700 hover:underline" data-testid="notification-read-all"
                onClick={() => act(() => api.post("/notifications/read-all", null, { params: clinician ? { clinician_id: clinician } : {} }))}>Mark all read</button>
            )}
            <button aria-label="Settings" data-testid="notification-settings-toggle" className="text-slate-500 hover:text-purple-700"
              onClick={() => (view === "settings" ? setView("list") : openSettings())}>
              {view === "settings" ? <X className="h-4 w-4" /> : <Settings className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {view === "settings" && settings && (
          <div className="p-4 space-y-4" data-testid="notification-settings">
            {SETTINGS.map(([k, label, hint]) => (
              <div key={k} className="flex items-center justify-between gap-4">
                <div><div className="text-sm font-medium text-slate-900">{label}</div><div className="text-xs text-slate-500">{hint}</div></div>
                <Switch checked={!!settings[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`setting-${k}`} />
              </div>
            ))}
            <p className="text-xs text-slate-500">Switched-off types are still saved on the patient (Level activity tab) but create no notification.</p>
          </div>
        )}

        {view === "list" && (<>
          <div className="px-4 py-2 flex flex-wrap gap-1.5 border-b border-slate-100">
            {CHIPS.map(([k, label]) => (
              <button key={k} type="button" data-testid={`chip-${k}`} onClick={() => { setChip(chip === k ? null : k); setPage(1); }}
                className={`text-xs px-2.5 py-1 rounded-full border ${chip === k ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-600 border-slate-200 hover:bg-purple-50"}`}>
                {label}
              </button>
            ))}
            {user?.role === "admin" && (
              <select className="ml-auto text-xs border border-slate-200 rounded px-1.5" value={clinician} data-testid="clinician-filter"
                onChange={(e) => { setClinician(e.target.value); setPage(1); }}>
                <option value="">All clinicians</option>
                {clinicians.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
          <div className="max-h-[55vh] overflow-y-auto divide-y divide-slate-100">
            {data.items.length === 0 && <div className="p-6 text-sm text-center text-slate-500">Nothing to review.</div>}
            {data.items.map((n) => {
              const groups = groupByModule(n.lines || []);
              const isOpen = !!expanded[n.id];
              return (
                <div key={n.id} data-testid={`notification-${n.id}`} className={`px-4 py-3 ${n.read ? "bg-white" : "bg-purple-50/60"}`}>
                  <div className="flex gap-2 items-start">
                    <button type="button" className="mt-0.5" aria-label="Show lines" onClick={() => setExpanded({ ...expanded, [n.id]: !isOpen })}>
                      {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 truncate">{n.patient_name || "Headset"}</span>
                        <span data-testid={`severity-${n.id}`} className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${SEV[n.severity]?.cls}`}>{SEV[n.severity]?.label}</span>
                        <span className="text-[11px] text-slate-500">{Object.keys(groups).length} module{Object.keys(groups).length === 1 ? "" : "s"} · {new Date(n.updated_at).toLocaleString()}</span>
                      </div>
                      {n.patient_id && (
                        <button className="text-xs text-purple-700 hover:underline inline-flex items-center mt-0.5" onClick={() => go(n, `/patients/${n.patient_id}`)}>
                          Open patient <ArrowUpRight className="h-3 w-3" />
                        </button>
                      )}
                      {isOpen && Object.entries(groups).map(([game, lines]) => (
                        <div key={game} className="mt-2 border-l-2 border-purple-200 pl-3">
                          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                            {game}
                            {lines[0].headset_id && (
                              <button className="font-normal text-purple-700 hover:underline inline-flex items-center" onClick={() => go(n, `/headsets/${lines[0].headset_id}`)}>
                                Module settings <ArrowUpRight className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          {lines.map((l, i) => <div key={i} className="text-sm text-slate-800" data-testid={`line-${l.type}`}>{l.message}</div>)}
                        </div>
                      ))}
                    </div>
                    {!n.read && <button title="Mark read" aria-label="Mark read" className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" onClick={() => act(() => api.post(`/notifications/${n.id}/read`))}><Check className="h-4 w-4 text-slate-500" /></button>}
                    <button title="Dismiss" aria-label="Dismiss" data-testid={`dismiss-${n.id}`} className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100" onClick={() => act(() => api.delete(`/notifications/${n.id}`))}><X className="h-4 w-4 text-slate-500" /></button>
                  </div>
                </div>
              );
            })}
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 text-xs">
              <button disabled={page <= 1} className="disabled:opacity-40 text-purple-700" onClick={() => setPage(page - 1)}>Previous</button>
              <span className="text-slate-500">Page {page} of {pages}</span>
              <button disabled={page >= pages} className="disabled:opacity-40 text-purple-700" onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </>)}
      </PopoverContent>
    </Popover>
  );
}
